import {
  Button,
  Cell,
  Column,
  MenuItem,
  MenuTrigger,
  Row,
  Table,
  TableBody,
  TableHeader,
} from '@eleveo/components-react';
import { BinIcon, PencilIcon, ThreeDotsIcon, UserAddIcon } from '@eleveo/icons-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LIST_OF_QUEUES_CONTAINER_TAG, getQueuePropTag } from './constants';
import { normalizeAcdQueues } from './queuesUtils';
import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { MultipleItemsLabel } from '../../components/LayoutNext/components/MultipleItemsLabel';
import { Queue, ServiceLevel } from '../../graphql/components';
import { AcdQueuesResult, QueuesResult, SkillsAllResult } from '../../graphql/types';
import { recordBy } from '../../utils/array';
import { formatTimezoneIdToName } from '../../utils/timezoneFormatter';

import type { ColumnProps } from '@eleveo/components-react';

export interface QueuesTableProps {
  queuesResult: QueuesResult;
  acdQueuesResult: AcdQueuesResult;
  skillsResult: SkillsAllResult;
  onAssignQueue: (q: Queue) => void;
  onEditQueue: (q: Queue) => void;
  onDeleteQueue: (q: Queue) => void;
  activeId?: string;
}

export const QueuesTable: React.FC<QueuesTableProps> = ({
  activeId,
  queuesResult,
  acdQueuesResult,
  skillsResult,
  onAssignQueue,
  onEditQueue,
  onDeleteQueue,
}) => {
  const { locale, userHasPermissions } = useAuth();
  const { t } = useTranslation();

  const hasEditPermission = userHasPermissions([Permissions.EditQueue]);
  const hasDeletePermission = userHasPermissions([Permissions.DeleteQueue]);
  const hasPermissions = hasEditPermission || hasDeletePermission;

  const columns: (ColumnProps<any> & { key: React.Key })[] = [
    {
      key: 'queue',
      children: t('queue.queue'),
    },
    {
      key: 'channelType',
      children: t('queue.channelType.channelType'),
    },
    {
      key: 'tag',
      children: t('queue.queueTag'),
    },
    {
      key: 'acdQueue',
      children: t('queue.acdQueue'),
    },
    {
      key: 'timezone',
      children: t('timezone.timezone', { count: 1 }),
    },
    {
      key: 'dataInterval',
      children: t('queue.dataInterval', { count: 1 }),
    },
    {
      key: 'skill',
      children: t('skill.skill'),
    },
    {
      key: 'serviceLevel',
      children: t('queue.serviceLevel'),
    },
  ];

  if (hasPermissions) {
    columns.push({
      key: 'actions',
      children: t('layout.action.actions'),
      isActions: true,
      hideHeader: true,
    });
  }

  const getServiceLevel = (serviceLevel: ServiceLevel) => {
    // Loose equality to cover both `null` and `undefined`
    if (serviceLevel.percentage == null || serviceLevel.interval == null) {
      return null;
    }

    const { percentage, interval } = serviceLevel;

    return t('queue.serviceLevelPercentageInInterval', {
      percentage: (percentage / 100).toLocaleString(locale, { maximumFractionDigits: 2, style: 'percent' }),
      interval: interval.toLocaleString(locale),
    });
  };

  const queuesWithMetadata = useMemo(() => {
    if (acdQueuesResult.loading || queuesResult.loading || skillsResult.loading) {
      // all data needs to be loaded first
      return [];
    }

    const normalizedAcdQueuesRecordObj = recordBy(normalizeAcdQueues(acdQueuesResult?.data?.acdQueues ?? []), 'id');
    const skillsRecordObj = recordBy(skillsResult.data?.skills ?? [], 'id');

    const getAcdQueues = (acdQueueIds: Queue['acdQueueIds']) =>
      acdQueueIds?.reduce((acdQueues, acdQueueId) => {
        const normalizedAcdQueue = normalizedAcdQueuesRecordObj[acdQueueId];

        if (!normalizedAcdQueue) {
          return acdQueues;
        }

        return [...acdQueues, normalizedAcdQueue];
      }, [] as ReturnType<typeof normalizeAcdQueues>);

    return queuesResult.data?.queues.map((queue) => ({
      ...queue,
      acdQueues: getAcdQueues(queue.acdQueueIds),
      skill: queue.skillId && skillsRecordObj[queue.skillId]?.name,
    }));
  }, [acdQueuesResult, queuesResult, skillsResult]);

  return (
    <Table
      aria-label={t('queue.queues')}
      highlightedKey={activeId}
      isStickyHeader
      testId={LIST_OF_QUEUES_CONTAINER_TAG}
    >
      <TableHeader columns={columns}>{(column) => <Column {...column} key={column.key} />}</TableHeader>
      <TableBody items={queuesWithMetadata ?? []}>
        {(queue) => (
          <Row key={queue.id}>
            {(columnKey) => {
              switch (columnKey) {
                case 'queue':
                  return (
                    <Cell>
                      <strong>{queue.name}</strong>
                    </Cell>
                  );

                case 'channelType':
                  return <Cell>{t(`queue.channelType.${queue.channelType}`)}</Cell>;

                case 'tag':
                  return <Cell>{queue.tag}</Cell>;

                case 'acdQueue':
                  return (
                    <Cell key={columnKey}>
                      <MultipleItemsLabel items={queue.acdQueues} />
                    </Cell>
                  );
                case 'timezone':
                  return <Cell>{formatTimezoneIdToName(queue.timezone)}</Cell>;

                case 'dataInterval':
                  return (
                    <Cell>
                      {queue.historicalDataInterval &&
                        t(`dataImport.historicalDataInterval.${queue.historicalDataInterval}`)}
                    </Cell>
                  );
                case 'skill':
                  return <Cell>{queue.skill || null}</Cell>;
                case 'serviceLevel':
                  return <Cell>{queue.serviceLevel ? getServiceLevel(queue.serviceLevel) : null}</Cell>;
                case 'actions':
                  return (
                    <Cell>
                      <MenuTrigger
                        items={
                          [
                            hasEditPermission && {
                              id: '1',
                              icon: <UserAddIcon />,
                              onClick: () => onAssignQueue(queue),
                              testId: getQueuePropTag('assign'),
                              text: t('person.assignPeople'),
                            },
                            hasEditPermission && {
                              id: '2',
                              icon: <PencilIcon />,
                              onClick: () => onEditQueue(queue),
                              testId: getQueuePropTag('edit'),
                              text: t('layout.action.edit'),
                            },
                            hasDeletePermission && {
                              id: '3',
                              icon: <BinIcon />,
                              onClick: () => onDeleteQueue(queue),
                              testId: getQueuePropTag('delete'),
                              text: t('layout.action.delete'),
                              isDisabled: queue.used,
                            },
                          ].filter(Boolean) as MenuItem[]
                        }
                      >
                        <Button
                          aria-label={t('layout.action.actions')}
                          isSmall
                          testId={getQueuePropTag('actions')}
                          variant="quiet"
                        >
                          <ThreeDotsIcon />
                        </Button>
                      </MenuTrigger>
                    </Cell>
                  );

                default:
                  return <Cell>&nbsp;</Cell>;
              }
            }}
          </Row>
        )}
      </TableBody>
    </Table>
  );
};
